import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import organizationService, { type Department } from '../services/organizationService'

export const useOrganizationStore = defineStore('organization', () => {
  // Departments - store full department objects from backend
  const departmentsData = ref<Department[]>([])

  // Computed property to get department names for backward compatibility
  const departments = computed(() => departmentsData.value.map(d => d.name))

  // Company Information
  const companyName = ref('')
  
  // Initialize logoUrl from localStorage if available
  const storedLogoUrl = localStorage.getItem('organization_logo_url')
  const logoUrl = ref<string | null>(storedLogoUrl ? storedLogoUrl : null)
  
  // Watch logoUrl and persist to localStorage whenever it changes
  watch(logoUrl, (newValue) => {
    if (newValue) {
      localStorage.setItem('organization_logo_url', newValue)
    } else {
      localStorage.removeItem('organization_logo_url')
    }
  })

  // Brand Compliance
  const restrictedTopics = ref<string[]>([])

  // Actions
  async function addDepartment(department: string): Promise<void> {
    if (!department.trim()) {
      throw new Error('Department name cannot be empty')
    }
    
    // Check if department already exists
    if (departments.value.includes(department.trim())) {
      throw new Error('Department already exists')
    }

    try {
      const newDepartment = await organizationService.createDepartment(department.trim())
      departmentsData.value.push(newDepartment)
      console.log('[OrganizationStore.addDepartment] Success:', newDepartment.name)
    } catch (error) {
      console.error('[OrganizationStore.addDepartment] Failed:', error)
      throw error
    }
  }

  async function removeDepartment(departmentName: string): Promise<void> {
    const dept = departmentsData.value.find(d => d.name === departmentName)
    if (!dept) {
      throw new Error('Department not found')
    }

    try {
      await organizationService.deleteDepartment(dept.id)
      const index = departmentsData.value.findIndex(d => d.id === dept.id)
      if (index !== -1) {
        departmentsData.value.splice(index, 1)
      }
      console.log('[OrganizationStore.removeDepartment] Success:', departmentName)
    } catch (error) {
      console.error('[OrganizationStore.removeDepartment] Failed:', error)
      throw error
    }
  }

  async function updateDepartment(departmentId: string, newName: string): Promise<void> {
    if (!newName.trim()) {
      throw new Error('Department name cannot be empty')
    }

    // Check if new name already exists (excluding the current department)
    const existingDept = departmentsData.value.find(
      d => d.name.toLowerCase() === newName.trim().toLowerCase() && d.id !== departmentId
    )
    if (existingDept) {
      throw new Error('A department with this name already exists')
    }

    try {
      const updatedDepartment = await organizationService.updateDepartment(departmentId, newName.trim())
      const index = departmentsData.value.findIndex(d => d.id === departmentId)
      if (index !== -1) {
        departmentsData.value[index] = updatedDepartment
      }
      console.log('[OrganizationStore.updateDepartment] Success:', updatedDepartment.name)
    } catch (error) {
      console.error('[OrganizationStore.updateDepartment] Failed:', error)
      throw error
    }
  }

  function addRestrictedTopic(topic: string) {
    if (topic.trim() && !restrictedTopics.value.includes(topic.trim())) {
      restrictedTopics.value.push(topic.trim())
    }
  }

  function removeRestrictedTopic(topic: string) {
    const index = restrictedTopics.value.indexOf(topic)
    if (index !== -1) {
      restrictedTopics.value.splice(index, 1)
    }
  }

  function updateCompanyName(name: string) {
    companyName.value = name
  }

  // Logo upload/delete actions
  async function uploadLogo(file: File): Promise<void> {
    try {
      const response = await organizationService.uploadLogo(file)
      logoUrl.value = response.logo_url
    } catch (error) {
      console.error('[OrganizationStore.uploadLogo] Failed:', error)
      throw error
    }
  }

  async function deleteLogo(): Promise<void> {
    try {
      await organizationService.deleteLogo()
      logoUrl.value = null
    } catch (error) {
      console.error('[OrganizationStore.deleteLogo] Failed:', error)
      throw error
    }
  }

  async function fetchOrganization(): Promise<void> {
    try {
      const org = await organizationService.getOrganization()
      if (org) {
        companyName.value = org.name || ''
        logoUrl.value = org.logo_url || null
      } else {
        // User is not part of an organization - set defaults
        companyName.value = ''
        logoUrl.value = null
        console.log('[OrganizationStore.fetchOrganization] User is not part of an organization')
      }
    } catch (error) {
      console.error('[OrganizationStore.fetchOrganization] Failed:', error)
      // Don't throw - allow app to continue without organization data
      companyName.value = ''
      logoUrl.value = null
    }
  }

  async function fetchDepartments(): Promise<void> {
    try {
      const depts = await organizationService.getDepartments()
      departmentsData.value = depts
      console.log('[OrganizationStore.fetchDepartments] Loaded', depts.length, 'departments')
    } catch (error) {
      console.error('[OrganizationStore.fetchDepartments] Failed:', error)
      // Don't throw - set empty array to allow app to continue
      departmentsData.value = []
    }
  }

  // Helper function to get department ID by name
  function getDepartmentId(departmentName: string): string | null {
    const dept = departmentsData.value.find(d => d.name === departmentName)
    return dept?.id || null
  }

  // Helper function to get department name by ID
  function getDepartmentName(departmentId: string): string | null {
    const dept = departmentsData.value.find(d => d.id === departmentId)
    return dept?.name || null
  }

  return {
    // State
    departments,
    departmentsData,
    companyName,
    logoUrl,
    restrictedTopics,

    // Actions
    addDepartment,
    removeDepartment,
    updateDepartment: updateDepartment as (departmentId: string, newName: string) => Promise<void>,
    addRestrictedTopic,
    removeRestrictedTopic,
    updateCompanyName,
    uploadLogo,
    deleteLogo,
    fetchOrganization,
    fetchDepartments,
    getDepartmentId,
    getDepartmentName
  }
})
